// Live countdown for the Daily deals evening window (store time zone).
//
// Attributes on <s-deal-countdown>:
//   data-time-zone   IANA zone of the window, e.g. "Europe/Amsterdam" (default)
//   data-start-hour  hour the deals go live (default 18)
//   data-end-hour    hour the deals end (default 23)
// Markup hooks inside it:
//   [data-countdown-value]          receives HH:MM:SS
//   [data-countdown-state="before"] label shown before the window today ("Deals start in")
//   [data-countdown-state="live"]   label shown during the window ("Deals end in")
//   [data-countdown-state="after"]  label shown after the window ("Next drop in", counts to tomorrow's start)
// The element stays hidden until the first tick, so no stale time shows without JS.

const pad = (value) => String(value).padStart(2, '0');
const DAY = 86400;

class DealCountdown extends HTMLElement {
  connectedCallback() {
    this.timeZone = this.dataset.timeZone || 'Europe/Amsterdam';
    this.start = Number(this.dataset.startHour ?? 18) * 3600;
    this.end = Number(this.dataset.endHour ?? 23) * 3600;
    this.output = this.querySelector('[data-countdown-value]');
    this.labels = [...this.querySelectorAll('[data-countdown-state]')];
    if (!this.output) return;
    this.formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: this.timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    this.tick();
    this.hidden = false;
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  // Seconds since midnight on the zone's wall clock (DST-safe).
  nowSeconds() {
    const parts = this.formatter.formatToParts(new Date());
    const get = (type) => Number(parts.find((part) => part.type === type)?.value || 0);
    return get('hour') * 3600 + get('minute') * 60 + get('second');
  }

  tick() {
    const now = this.nowSeconds();
    let state;
    let left;
    if (now < this.start) {
      state = 'before';
      left = this.start - now;
    } else if (now < this.end) {
      state = 'live';
      left = this.end - now;
    } else {
      state = 'after';
      left = DAY - now + this.start;
    }
    if (state !== this.state) {
      this.state = state;
      this.dataset.state = state;
      for (const label of this.labels) label.hidden = label.dataset.countdownState !== state;
    }
    const hours = Math.floor(left / 3600);
    const minutes = Math.floor((left % 3600) / 60);
    const seconds = left % 60;
    this.output.textContent = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
}

if (!customElements.get('s-deal-countdown')) customElements.define('s-deal-countdown', DealCountdown);
